import { createRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { PwaApp } from './pwa/pwaApp'
import React from 'react'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/toaster'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { startServiceWorkerKeepAlive } from './utils/serviceWorkerKeepAlive'

// Após um novo deploy, abas antigas pedem arquivos que não existem mais e a tela fica em branco.
// Recarrega a página uma vez para buscar a versão nova (o intervalo evita loop de recarga).
window.addEventListener('vite:preloadError', (event) => {
  try {
    const last = Number(sessionStorage.getItem('preloadErrorReload') || 0);
    if (Date.now() - last < 30_000) return;
    sessionStorage.setItem('preloadErrorReload', String(Date.now()));
  } catch {
    // sessionStorage indisponível: recarrega mesmo assim
  }
  event.preventDefault();
  window.location.reload();
})

// Registrar Service Worker para PWA
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // Detectar modo PWA/standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
    const urlParams = new URLSearchParams(window.location.search);
    const pwaParam = urlParams.has('pwa');
    const preferPwa = localStorage.getItem('preferredMode') === 'pwa';
    const isMobile = window.innerWidth <= 768 || /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isPWAContext = isStandalone || pwaParam || preferPwa;

    // Escolher Service Worker conforme contexto
    const swFile = isPWAContext ? '/pwa-sw.js' : (isMobile ? '/mobile-sw.js' : '/sw.js');
    
    navigator.serviceWorker.register(swFile)
      .then((registration) => {
        console.log(`${isPWAContext ? 'PWA ' : isMobile ? 'Mobile ' : ''}SW registrado: `, registration);
        
        // CRÍTICO para iOS: Iniciar keep-alive para manter SW ativo quando app está fechado
        if (isMobile || isPWAContext) {
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'activated') {
                  // SW ativado, iniciar keep-alive
                  startServiceWorkerKeepAlive();
                }
              });
            }
          });
          
          // Iniciar keep-alive imediatamente se SW já estiver ativo
          if (registration.active) {
            startServiceWorkerKeepAlive();
          } else {
            // Aguardar SW estar pronto
            navigator.serviceWorker.ready.then(() => {
              startServiceWorkerKeepAlive();
            });
          }
        }
        
        // Verificar atualizações do service worker
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // Nova versão disponível
                console.log('Nova versão do app disponível!');
              }
            });
          }
        });
      })
      .catch((registrationError) => {
        console.log(`${isPWAContext ? 'PWA ' : isMobile ? 'Mobile ' : ''}SW falhou: `, registrationError);
    });
  });
}

// Render dinâmico: se em modo standalone/pwa, montar a versão PWA
const rootEl = document.getElementById('root')!;
const queryClient = new QueryClient();
const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone === true;
const pwaParam = new URLSearchParams(window.location.search).has('pwa');
const preferPwa = localStorage.getItem('preferredMode') === 'pwa';
const isPWAContext = isStandalone || pwaParam || preferPwa;

if (isStandalone) {
  // Persistir preferência para futuras aberturas
  localStorage.setItem('preferredMode', 'pwa');
}

if (isPWAContext) {
  createRoot(rootEl).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <PwaApp />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );
} else {
  createRoot(rootEl).render(<App />);
}
